import { REPORT_TYPE, type Message } from '../types';

// A message counts toward "signal activity" if it has a type - a plain
// typed chat message doesn't (see Message.type's comment) - EXCEPT
// REPORT_TYPE: a report is tagged with a type so it can be filtered into
// its own tab (see ReportsScreen.tsx), but it isn't a template-signal tap
// and shouldn't inflate this chart. Central so every caller (this file's
// own functions, and SignalActivityChart.tsx's own "signalMessages"
// filter) agrees on the same definition.
export function isChartableSignal(m: Message): boolean {
  return !!m.type && m.type !== REPORT_TYPE;
}

export const CHART_INTERVALS = ['Minutes', 'Day', 'Week', 'Month', 'Year', 'All'] as const;
export type ChartInterval = (typeof CHART_INTERVALS)[number];

// The strings above are internal keys (buildBuckets switches on them, so
// they must stay stable); these are what the user sees on the segmented
// control. Naming rule: each label is the plural of the unit one bar
// represents - i.e. "what this chart shows you". "Hours" is a short
// intraday window whose exact span (15 min - 12 h) is picked with the
// separate MINUTES_SPANS control; "Days" is the last 7 days, "Weeks" the
// last 8 weeks, "Months" the last 6 months, "Years" the last 5 years.
// Keep in sync with iOS ChartInterval.label in ChartBuckets.swift.
export const INTERVAL_LABELS: Record<ChartInterval, string> = {
  Minutes: 'Hours',
  Day: 'Days',
  Week: 'Weeks',
  Month: 'Months',
  Year: 'Years',
  All: 'All',
};

// Only shown/used when interval === 'Minutes' - how far back the minute-
// granularity chart looks.
export const MINUTES_SPANS = [
  { value: '12h', label: '12 hours', minutes: 12 * 60 },
  { value: '6h', label: '6 hours', minutes: 6 * 60 },
  { value: '180m', label: '180 min', minutes: 180 },
  { value: '60m', label: '60 min', minutes: 60 },
  { value: '30m', label: '30 min', minutes: 30 },
  { value: '15m', label: '15 min', minutes: 15 },
] as const;
export type MinutesSpan = (typeof MINUTES_SPANS)[number]['value'];

export interface TimeBucket {
  start: number;
  end: number;
  label: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

// Signal-message timestamps only - a chart built from all-time chatter would
// be dominated by plain chat messages, but this chart exists to show signal
// activity (template taps), so "All" spans just the messages that have a
// `type` (see Message.type's comment in types.ts).
function signalTimestamps(messages: Message[]): number[] {
  return messages.filter(isChartableSignal)?.map((m) => new Date(m.date).getTime());
}

// Builds a fixed number of equal-length buckets ending "now", one per unit
// of the chosen interval (e.g. the last 7 days, the last 8 weeks) - except
// "All", which instead spans from the earliest signal message to now,
// divided into a fixed bucket count, so a channel's whole history fits on
// one chart no matter how old it is.
export function buildBuckets(
  interval: ChartInterval,
  messages: Message[],
  options: { now?: Date; minutesSpan?: MinutesSpan; earliestMs?: number } = {}
): TimeBucket[] {
  const now = options.now ?? new Date();

  if (interval === 'Minutes') {
    const spanMinutes = MINUTES_SPANS.find((s) => s.value === (options.minutesSpan ?? '60m'))?.minutes ?? 60;
    // Whole-minute buckets, aligned to the LOCAL clock. The server's minute
    // rollups are whole-minute slots, so a bucket boundary that falls mid-
    // minute (the old "now minus span" window: 2.5-minute buckets for 15 min,
    // edges sliding every second) split slots unevenly and made the bars
    // jump between renders. 15 min -> 5 x 3 min; every other span -> 6.
    // The last bucket is the one containing `now` (still filling), so the
    // window is the last `span` minutes up to the end of that bucket.
    // Keep in sync with iOS ChartBuckets.buildBuckets.
    const bucketCount = spanMinutes === 15 ? 5 : 6;
    const bucketMs = (spanMinutes / bucketCount) * MINUTE_MS;
    const offsetMs = -now.getTimezoneOffset() * MINUTE_MS;
    const end = Math.floor((now.getTime() + offsetMs) / bucketMs) * bucketMs - offsetMs + bucketMs;
    const windowStart = end - bucketCount * bucketMs;
    return Array.from({ length: bucketCount }, (_, i) => {
      const start = windowStart + i * bucketMs;
      return {
        start,
        end: start + bucketMs,
        label: new Date(start).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
      };
    });
  }
  if (interval === 'Day') {
    const today = startOfDay(now);
    return Array.from({ length: 7 }, (_, i) => {
      const start = today - (6 - i) * DAY_MS;
      return { start, end: start + DAY_MS, label: new Date(start).toLocaleDateString(undefined, { weekday: 'short' }) };
    });
  }
  if (interval === 'Week') {
    const dayIndex = now.getDay();
    const diffToMonday = (dayIndex + 6) % 7;
    const thisWeekStart = startOfDay(new Date(now.getTime() - diffToMonday * DAY_MS));
    return Array.from({ length: 8 }, (_, i) => {
      const start = thisWeekStart - (7 - i) * 7 * DAY_MS;
      const d = new Date(start);
      return { start, end: start + 7 * DAY_MS, label: `${d.getMonth() + 1}/${d.getDate()}` };
    });
  }
  if (interval === 'Month') {
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      const start = d.getTime();
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime();
      return { start, end, label: d.toLocaleDateString(undefined, { month: 'short' }) };
    });
  }
  if (interval === 'Year') {
    return Array.from({ length: 5 }, (_, i) => {
      const year = now.getFullYear() - (4 - i);
      return { start: new Date(year, 0, 1).getTime(), end: new Date(year + 1, 0, 1).getTime(), label: String(year) };
    });
  }

  // All
  const times = signalTimestamps(messages);
  // Server rollups know the channel's true earliest signal even when only
  // the newest page of messages is loaded client-side.
  if (options.earliestMs !== undefined) times.push(options.earliestMs);
  const bucketCount = 8;
  if (times.length === 0) {
    const start = startOfDay(now);
    return [{ start, end: start + DAY_MS, label: 'Now' }];
  }
  const earliest = Math.min(...times);
  const latest = Math.max(now.getTime(), ...times);
  const span = Math.max(latest - earliest, DAY_MS);
  const bucketMs = span / bucketCount;
  return Array.from({ length: bucketCount }, (_, i) => {
    const start = earliest + i * bucketMs;
    return {
      start,
      end: start + bucketMs,
      label: new Date(start).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    };
  });
}

// Counts, per bucket, how many of `messages` (already filtered to a single
// signal type or left as "all signals" by the caller) fall in that bucket's
// [start, end) window.
export function countByBucket(buckets: TimeBucket[], messages: Message[]): number[] {
  const times = signalTimestamps(messages);
  return buckets?.map((bucket) => times.filter((t) => t >= bucket.start && t < bucket.end).length);
}

// Pairs each signal message with its parsed numeric value (see Message.value's
// comment in types.ts) - a present-but-unparseable value, or none at all, is
// dropped rather than treated as 0, so a mixed set of valued/unvalued taps
// under "All signals" doesn't silently pad the sum with zeros for messages
// that never carried a number to begin with.
function signalValues(messages: Message[]): { time: number; value: number }[] {
  return messages
    .filter((m) => isChartableSignal(m) && m.value !== undefined)
    ?.map((m) => ({ time: new Date(m.date).getTime(), value: parseFloat(m.value as string) }))
    .filter((entry) => Number.isFinite(entry.value));
}

// True if any of `messages` carries a parseable numeric value - gates
// whether SignalActivityChart offers a "by value" metric at all (most
// templates never use it, so the toggle only appears once it'd do
// something).
export function hasNumericValues(messages: Message[]): boolean {
  return signalValues(messages).length > 0;
}

// Sums, per bucket, the numeric value of `messages` that fall in that
// bucket's [start, end) window - the "by value" counterpart to
// countByBucket (e.g. summing a revenue signal's dollar amounts instead of
// just counting how many times it was sent).
export function sumByBucket(buckets: TimeBucket[], messages: Message[]): number[] {
  const entries = signalValues(messages);
  return buckets?.map((bucket) =>
    entries.filter((e) => e.time >= bucket.start && e.time < bucket.end).reduce((sum, e) => sum + e.value, 0)
  );
}

// ---- entry-based aggregation (count / value / latest / average / min / max)
//
// A ChartEntry is a pre-summarised cell: either one signal message (count 1)
// or one server rollup slot (many signals folded together - see backend
// internal/chartroll). Aggregating entries instead of messages lets the
// chart paint from cached server rollups on first load and then fold live
// signals in on top, through the exact same code.

export const CHART_METRICS = ['count', 'sum', 'last', 'avg', 'min', 'max'] as const;
export type ChartMetric = (typeof CHART_METRICS)[number];

export const METRIC_LABELS: Record<ChartMetric, string> = {
  count: 'By count',
  sum: 'By value',
  last: 'Latest',
  avg: 'Average',
  min: 'Min',
  max: 'Max',
};

// Only count and sum add up across series/buckets; the others don't, which
// matters for stacking (see SignalActivityChart).
export const isAdditiveMetric = (m: ChartMetric) => m === 'count' || m === 'sum';

export interface ChartEntry {
  type: string;
  t: number; // ms - the message's time, or a rollup slot's start
  count: number;
  nval: number; // observations that carried a numeric value
  sum: number;
  min: number;
  max: number;
  last: number;
  lastAt: number; // ms the `last` value was observed at
}

// One entry per chartable message; a message without a parseable value has
// nval 0 (it counts, but has no number to sum/average/min/max).
export function entriesFromMessages(messages: Message[]): ChartEntry[] {
  const out: ChartEntry[] = [];
  for (const m of messages) {
    if (!isChartableSignal(m)) continue;
    const t = new Date(m.at ?? m.date).getTime();
    if (!Number.isFinite(t)) continue;
    const v = m.value === undefined ? NaN : parseFloat(m.value);
    const has = Number.isFinite(v);
    out.push({ type: m.type!, t, count: 1, nval: has ? 1 : 0, sum: has ? v : 0, min: has ? v : 0, max: has ? v : 0, last: has ? v : 0, lastAt: t });
  }
  return out;
}

export function hasNumericEntries(entries: ChartEntry[]): boolean {
  return entries.some((e) => e.nval > 0);
}

// Reduces the entries falling in each bucket to one number. Empty buckets
// are 0, except "last" which carries the previous bucket's latest value
// forward - a raw reading stays true until the next one replaces it.
export function aggregateEntries(buckets: TimeBucket[], entries: ChartEntry[], metric: ChartMetric): number[] {
  // NaN = "no reading" (a gap the charts skip), NOT 0: a Latest/Average/Min/Max
  // of 0 in an empty bucket is a made-up number that drags the line to the
  // floor. count/sum are genuinely 0 there. `last` carries the previous reading
  // forward (a raw value stays true until replaced) but stays NaN before the
  // first reading in the window.
  let carried = NaN;
  // For "Latest", the true latest reading might have arrived just before
  // this window even opened - a channel that only reports every couple of
  // hours has no arrival at all inside a 15/30/60-minute window. Seeding
  // from the most recent value strictly before the window carries that
  // forward instead of a misleading "no reading" 0 that's really just an
  // artifact of where the window happens to start.
  if (metric === 'last' && buckets.length > 0) {
    const before = entries.filter((e) => e.nval > 0 && e.t < buckets[0].start);
    if (before.length > 0) carried = before.reduce((a, e) => (e.lastAt >= a.lastAt ? e : a)).last;
  }
  return buckets?.map((bucket) => {
    const inBucket = entries.filter((e) => e.t >= bucket.start && e.t < bucket.end);
    if (metric === 'count') return inBucket.reduce((n, e) => n + e.count, 0);
    const valued = inBucket.filter((e) => e.nval > 0);
    if (valued.length === 0) return metric === 'last' ? carried : metric === 'sum' ? 0 : NaN;
    switch (metric) {
      case 'sum':
        return valued.reduce((n, e) => n + e.sum, 0);
      case 'avg':
        return valued.reduce((n, e) => n + e.sum, 0) / valued.reduce((n, e) => n + e.nval, 0);
      case 'min':
        return Math.min(...valued.map((e) => e.min));
      case 'max':
        return Math.max(...valued.map((e) => e.max));
      case 'last': {
        carried = valued.reduce((a, e) => (e.lastAt >= a.lastAt ? e : a)).last;
        return carried;
      }
    }
  });
}

// The whole range as one bucket - the headline number next to the chart
// (total for count/sum, overall average/min/max/latest otherwise).
export function overallOfEntries(buckets: TimeBucket[], entries: ChartEntry[], metric: ChartMetric): number {
  if (buckets.length === 0) return 0;
  const whole: TimeBucket = { start: buckets[0].start, end: buckets[buckets.length - 1].end, label: '' };
  const v = aggregateEntries([whole], entries, metric)[0];
  return Number.isFinite(v) ? v : 0; // nothing in range: show 0, never "NaN"
}

// Format a metric number for display: counts are integers, values keep up
// to 2 decimals without trailing zeros.
export function formatMetric(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}
